import React, { useCallback } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { HStack } from "@design-system/HStack/HStack";
import classNames from "classnames";
import { FinancialConnectionWithAccounts } from "@common/types/financialConnection";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { isIntegrationConnection } from "@common/helpers/integrationConnection";
import { isNonFixablePlaidConnection } from "@common/helpers/financialConnection";
import {
    isIncompleteStripeConnection,
    isReadyStripeExpressConnection,
} from "@common/helpers/stripeConnection";
import { StripeExpressReturnTo } from "@common/schemas/domains/stripe/stripe-express.schema";
import { currencyFormatter } from "@common/helpers/currency";
import { getAnonymousAccountNumber } from "@common/helpers/bankAccount";
import { useIdCopy } from "@hooks/useIdCopy";
import { timeAgoInSentence } from "@helpers/date";
import { Button } from "@design-system/Button/Button";
import { AlertIcon, TrashIcon } from "@icons";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { FixFinancialConnection } from "../financialAccount/FixFinancialConnection";
import { FinancialAccountManagementDropdown } from "../financialAccount/FinancialAccountManagementDropdown";
import "./DisplayIntegrationAccount.scss";
import { useConnectAccount } from "../connectAccount/ConnectAccount.context";
import { FixIntegrationConnection } from "../integrationConnection/FixIntegrationConnection";
import { FinishStripeExpressSetup } from "../stripe/FinishStripeExpressSetup";
import { RemoveIntegrationAccount } from "./RemoveIntegrationAccount";

interface Props {
    className?: string;
    connection: FinancialConnectionWithAccounts | IntegrationConnection;
    integrationAccount: IntegrationAccount;
    noFooter?: boolean;
}

export const DisplayIntegrationAccount: React.FC<Props> = ({
    connection,
    integrationAccount,
    className,
    noFooter,
}) => {
    const { updateConnectAccountState } = useConnectAccount();
    const { getCopyProps } = useIdCopy({ label: "Financial Account" });

    const addMissingInfo = useCallback(() => {
        updateConnectAccountState({
            isConnecting: false,
            connection: !isIntegrationConnection(connection)
                ? connection
                : undefined,
            integrationConnection: isIntegrationConnection(connection)
                ? connection
                : undefined,
            connectionSaved: true,
            foreign: [],
        });
    }, [updateConnectAccountState, connection]);

    // A non-fixable Plaid connection renders its own indicator with a
    // dedicated tooltip, so the generic disconnected tooltip must stay off.
    const isNonFixablePlaid = isNonFixablePlaidConnection(connection);

    let accountTooltipContent: React.ReactNode;

    if (connection.error && !isNonFixablePlaid) {
        accountTooltipContent = (
            <>
                This account is disconnected. <br />
                {!!integrationAccount.balanceSyncedAt && (
                    <>
                        Balance updated{" "}
                        {timeAgoInSentence(integrationAccount.balanceSyncedAt)}.
                    </>
                )}
            </>
        );
    }

    const financialAccount = integrationAccount.financialAccount;
    const showMask = !financialAccount?.name?.match(/\d{4}$/);

    return (
        <section
            className={classNames(
                "integration-account",
                {
                    "integration-account--disabled": financialAccount?.disabled,
                    "integration-account--no-footer": noFooter,
                },
                className,
            )}
        >
            <aside className="integration-account__entity">
                {financialAccount?.entity && (
                    <EntityAvatar entity={financialAccount.entity} size="xs" />
                )}
            </aside>
            <main className="integration-account__content">
                <span>
                    <div {...getCopyProps(financialAccount?.id)}>
                        {financialAccount?.name}{" "}
                        {financialAccount?.accountNumberMask &&
                            showMask &&
                            getAnonymousAccountNumber(
                                financialAccount.accountNumberMask,
                                2,
                            )}
                    </div>
                    {financialAccount?.entity ? (
                        <small>
                            {financialAccount.isBusiness
                                ? "Business"
                                : "Personal"}
                        </small>
                    ) : (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={addMissingInfo}
                            leftIcon={
                                <AlertIcon className="icon-color-red-500" />
                            }
                        >
                            Add missing info
                        </Button>
                    )}
                </span>
            </main>

            {connection.error ? (
                <aside className="align-self-start">
                    <Tooltip content={accountTooltipContent}>
                        <span>
                            {isIntegrationConnection(connection) ? (
                                <FixIntegrationConnection
                                    integrationConnection={connection}
                                    variant="secondary"
                                    size="sm"
                                    className="broken-connection-link danger"
                                    leftIcon={
                                        <AlertIcon className="icon-color-red-500" />
                                    }
                                >
                                    Fix connection
                                </FixIntegrationConnection>
                            ) : (
                                <FixFinancialConnection
                                    connection={connection}
                                    variant="secondary"
                                    size="sm"
                                    className="broken-connection-link danger"
                                    leftIcon={<AlertIcon />}
                                >
                                    Fix connection
                                </FixFinancialConnection>
                            )}
                        </span>
                    </Tooltip>
                </aside>
            ) : null}

            {!connection.error &&
            !isIntegrationConnection(connection) &&
            isIncompleteStripeConnection(connection) ? (
                <aside className="align-self-start">
                    <Tooltip content="Finish Stripe setup to collect payments.">
                        <span>
                            <FinishStripeExpressSetup
                                connectionId={connection.id}
                                returnTo={StripeExpressReturnTo.INTEGRATIONS}
                            />
                        </span>
                    </Tooltip>
                </aside>
            ) : null}

            {!isIntegrationConnection(connection) &&
            isReadyStripeExpressConnection(connection) ? (
                <aside className="align-self-start">
                    <small className="integration-account__paymentsBadge">
                        Payments
                    </small>
                </aside>
            ) : null}

            <HStack gap={24} className="ml-auto">
                <aside>
                    {financialAccount?.balance &&
                    !isNaN(financialAccount.balance) ? (
                        <span className="font-weight-medium">
                            {currencyFormatter.format(financialAccount.balance)}
                        </span>
                    ) : null}
                </aside>
                <aside className="integration-account__actions">
                    {financialAccount?.entity ? (
                        <FinancialAccountManagementDropdown
                            account={financialAccount}
                            connection={connection}
                        />
                    ) : (
                        <RemoveIntegrationAccount
                            account={integrationAccount}
                            variant="secondary"
                        >
                            <TrashIcon />
                        </RemoveIntegrationAccount>
                    )}
                </aside>
            </HStack>
        </section>
    );
};
